import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { loadAllGuardsForExport } from '../pages/manage_guard/service/guardService';
import { initializeTodayAttendanceBatch, getCurrentDateInfo } from '../pages/attendance/service/attendanceService';

const STORAGE_KEY = 'attendance_last_init_date';

/**
 * Get the last initialization date from localStorage
 */
const getLastInitDate = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Set the last initialization date in localStorage
 */
const setLastInitDate = (date: string): void => {
  try {
    localStorage.setItem(STORAGE_KEY, date);
  } catch {
    // Ignore localStorage errors
  }
};

/**
 * Hook to initialize today's attendance in the background when dashboard loads
 * This runs silently without blocking the UI
 */
export const useAttendanceBackgroundInit = () => {
  const { user, companyData } = useAuth();
  const initializedRef = useRef(false);

  useEffect(() => {
    // Only run once per day and only if user is authenticated
    if (!user || initializedRef.current) return;

    const currentDate = getCurrentDateInfo();
    const todayKey = `${currentDate.year}-${currentDate.month}-${currentDate.day}`;

    // Check localStorage, but don't skip if collection was deleted
    // We'll still check the database in initializeTodayAttendanceBatch
    const lastInitDate = getLastInitDate();
    if (lastInitDate === todayKey && initializedRef.current) {
      return;
    }

    const companyId = companyData?.id || user?.uid;
    if (!companyId) return;

    // Mark as initializing to prevent duplicate runs
    initializedRef.current = true;

    // Run initialization in background (don't await)
    const initAttendance = async () => {
      try {
        // Load all guards
        const guards = await loadAllGuardsForExport(companyId);
        
        if (guards.length === 0) return;

        // Initialize today's attendance for all guards using batch writes
        const guardIds = guards.map(g => g.id);
        await initializeTodayAttendanceBatch(guardIds, companyId);
        
        // Mark as initialized for today in localStorage
        setLastInitDate(todayKey);
        
        console.log(`Background attendance initialization completed for ${guards.length} guards`);
      } catch (error) {
        console.error('Background attendance initialization error:', error);
        // Reset flag on error so it can retry
        initializedRef.current = false;
        // Don't show error to user - this is background operation
      }
    };

    // Run in background with a small delay to not interfere with initial page load
    const timeoutId = setTimeout(() => {
      initAttendance();
    }, 2000); // Wait 2 seconds after dashboard loads

    return () => {
      clearTimeout(timeoutId);
    };
  }, [user, companyData]);
};

